/**
 * Epic-specific API hooks and utilities
 * Provides React Query hooks for epic data fetching and batch operations
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "../components/AppContext";
import { apiFetch } from "./api";
import {
  AvailableEpicsResponse,
  EpicListQueryOptions,
  EpicDetailsQueryOptions,
  BatchStatusQueryOptions,
  EpicDetailsData,
  EnhancedBatchStatusResponse,
  BatchQueueRequest,
  BatchQueueResponse,
  EpicAnalysisResponse,
  CosmosEpicAnalysisDocument,
  EpicApiError,
  BatchOperationError,
} from "../types";
import {
  EpicAnalyticsQueryOptions,
  EpicAnalyticsResponse,
  EpicAnalyticsData,
  EpicAnalyticsRequest,
} from "../types/epic-analytics-types";

// ===== Query Keys =====

/**
 * Query key factory for epic-related queries
 */
export const epicQueryKeys = {
  all: ["epics"] as const,
  lists: () => [...epicQueryKeys.all, "list"] as const,
  list: (filters: {
    projectKey?: string;
    startDate?: string;
    endDate?: string;
  }) => [...epicQueryKeys.lists(), filters] as const,
  details: () => [...epicQueryKeys.all, "detail"] as const,
  detail: (epicKey: string) => [...epicQueryKeys.details(), epicKey] as const,
  batches: () => [...epicQueryKeys.all, "batch"] as const,
  batch: (batchId: string) => [...epicQueryKeys.batches(), batchId] as const,
  analytics: () => [...epicQueryKeys.all, "analytics"] as const,
  analyticsData: (filters: {
    projectKey?: string;
    year?: number;
    quarter?: "all" | 1 | 2 | 3 | 4;
  }) => [...epicQueryKeys.analytics(), filters] as const,
};

// ===== Epic List Hooks =====

/**
 * Hook to fetch available epics with analysis status
 */
export function useAvailableEpics(
  options: EpicListQueryOptions
): UseQueryResult<AvailableEpicsResponse, EpicApiError> {
  const apiUrl = useApi();
  const { projectKey, startDate, endDate, enabled = true } = options;

  return useQuery({
    queryKey: epicQueryKeys.list({ projectKey, startDate, endDate }),
    queryFn: async () => {
      if (!projectKey || !startDate || !endDate) {
        throw new Error("Project key, start date, and end date are required");
      }

      const params = new URLSearchParams({
        projectKey,
        startDate,
        endDate,
      });

      return apiFetch<AvailableEpicsResponse>(
        apiUrl,
        `/epics/available?${params.toString()}`
      );
    },
    enabled: enabled && !!projectKey && !!startDate && !!endDate,
    staleTime: 15 * 60 * 1000, // 15 minutes - epic lists don't change frequently
    cacheTime: 45 * 60 * 1000, // 45 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus for epic lists
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    // Performance optimizations
    keepPreviousData: true, // Keep previous data while fetching new data
    notifyOnChangeProps: ["data", "error", "isLoading"], // Only re-render on specific prop changes
    retry: (failureCount, error) => {
      // Don't retry on validation errors (4xx)
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
}

// ===== Epic Details Hooks =====

/**
 * Hook to fetch detailed epic information and analysis
 */
export function useEpicDetails(
  options: EpicDetailsQueryOptions
): UseQueryResult<CosmosEpicAnalysisDocument, EpicApiError> {
  const apiUrl = useApi();
  const { epicKey, enabled = true } = options;

  return useQuery({
    queryKey: epicQueryKeys.detail(epicKey),
    queryFn: async () => {
      if (!epicKey) {
        throw new Error("Epic key is required");
      }

      const response = await apiFetch<{
        success: boolean;
        data?: CosmosEpicAnalysisDocument;
        message?: string;
      }>(apiUrl, `/epics/${epicKey}`);

      if (!response.success || !response.data) {
        throw new Error(response.message || "Failed to fetch epic details");
      }

      return response.data;
    },
    enabled: enabled && !!epicKey,
    staleTime: 5 * 60 * 1000, // 5 minutes - keep data fresh for navigation
    cacheTime: 30 * 60 * 1000, // 30 minutes - keep in cache for back navigation
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: (failureCount, error) => {
      // Don't retry on not found errors
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as any).status;
        if (status === 404) {
          return false;
        }
      }
      return failureCount < 2;
    },
  });
}

// ===== Batch Operation Hooks =====

/**
 * Hook to fetch batch status with real-time updates
 */
export function useBatchStatus(
  options: BatchStatusQueryOptions
): UseQueryResult<EnhancedBatchStatusResponse, BatchOperationError> {
  const apiUrl = useApi();
  const { batchId, enabled = true, refetchInterval } = options;

  return useQuery({
    queryKey: epicQueryKeys.batch(batchId),
    queryFn: async () => {
      if (!batchId) {
        throw new Error("Batch ID is required");
      }

      return apiFetch<EnhancedBatchStatusResponse>(
        apiUrl,
        `/epics/batch/${batchId}/status`
      );
    },
    enabled: enabled && !!batchId,
    staleTime: 10 * 1000, // 10 seconds for batch status - needs to be fresh
    cacheTime: 5 * 60 * 1000, // 5 minutes - don't keep batch status too long
    refetchInterval: refetchInterval || 5000, // Poll every 5 seconds by default
    refetchOnWindowFocus: true, // Refetch batch status on window focus
    refetchOnMount: true, // Always refetch batch status on mount
    retry: (failureCount, error) => {
      // Don't retry on not found errors
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as any).status;
        if (status === 404) {
          return false;
        }
      }
      return failureCount < 2;
    },
  });
}

// ===== Mutation Hooks =====

/**
 * Hook to start batch analysis for multiple epics with optimistic updates
 */
export function useStartBatchAnalysis(options?: {
  enableOptimisticUpdates?: boolean;
}) {
  const apiUrl = useApi();
  const queryClient = useQueryClient();
  const { enableOptimisticUpdates = false } = options || {}; // Disabled by default for batch operations

  return useMutation({
    mutationFn: async (
      request: BatchQueueRequest
    ): Promise<BatchQueueResponse> => {
      // Apply optimistic update for batch operations if enabled
      let rollback: (() => void) | undefined;

      if (
        enableOptimisticUpdates &&
        request.projectKey &&
        request.startDate &&
        request.endDate
      ) {
        const queryKey = epicQueryKeys.list({
          projectKey: request.projectKey,
          startDate: request.startDate,
          endDate: request.endDate,
        });

        const previousData =
          queryClient.getQueryData<AvailableEpicsResponse>(queryKey);

        if (previousData?.success && previousData.data?.epics) {
          // Optimistically mark all unanalyzed epics as being processed
          queryClient.setQueryData(
            queryKey,
            (oldData: AvailableEpicsResponse | undefined) => {
              if (!oldData?.success || !oldData.data?.epics) {
                return oldData;
              }

              // Count unanalyzed epics that will be processed
              const unanalyzedEpics = oldData.data.epics.filter(
                (epic) => !epic.analysisStatus.isAnalyzed
              );

              if (unanalyzedEpics.length === 0) {
                return oldData; // No changes needed
              }

              const updatedEpics = oldData.data.epics.map((epic) => {
                if (!epic.analysisStatus.isAnalyzed) {
                  return {
                    ...epic,
                    analysisStatus: {
                      isAnalyzed: true,
                      analysisDate: new Date().toISOString(),
                      analysisId: null, // Will be updated when batch completes
                    },
                  };
                }
                return epic;
              });

              return {
                ...oldData,
                data: {
                  ...oldData.data,
                  epics: updatedEpics,
                  summary: {
                    ...oldData.data.summary,
                    analyzed:
                      oldData.data.summary.analyzed + unanalyzedEpics.length,
                    notAnalyzed: 0, // All epics are now analyzed
                  },
                },
              };
            }
          );

          // Create rollback function
          rollback = () => {
            queryClient.setQueryData(queryKey, previousData);
          };
        }
      }

      try {
        const result = await apiFetch<BatchQueueResponse>(
          apiUrl,
          "/epics/batch/queue",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(request),
          }
        );

        return result;
      } catch (error) {
        // Rollback optimistic update on error
        if (rollback) {
          rollback();
        }
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      // Only invalidate if optimistic updates are disabled
      if (!enableOptimisticUpdates) {
        queryClient.invalidateQueries({
          queryKey: epicQueryKeys.lists(),
        });
      }

      // If batch was created successfully, start polling for status
      if (data.success && data.batchId) {
        queryClient.invalidateQueries({
          queryKey: epicQueryKeys.batch(data.batchId),
        });
      }
    },
    onError: (error, variables) => {
      console.error("Failed to start batch analysis:", error);

      // Invalidate queries on error to ensure consistency
      if (variables.projectKey && variables.startDate && variables.endDate) {
        queryClient.invalidateQueries({
          queryKey: epicQueryKeys.list({
            projectKey: variables.projectKey,
            startDate: variables.startDate,
            endDate: variables.endDate,
          }),
        });
      }
    },
  });
}

/**
 * Hook to analyze a single epic with optimistic updates
 */
export function useAnalyzeEpic(options?: {
  projectKey?: string;
  startDate?: string;
  endDate?: string;
  enableOptimisticUpdates?: boolean;
}) {
  const apiUrl = useApi();
  const queryClient = useQueryClient();
  const {
    projectKey,
    startDate,
    endDate,
    enableOptimisticUpdates = true,
  } = options || {};

  return useMutation({
    mutationFn: async (epicKey: string): Promise<EpicAnalysisResponse> => {
      if (!epicKey) {
        throw new Error("Epic key is required");
      }

      // Apply optimistic update if enabled and we have the required context
      let rollback: (() => void) | undefined;

      if (enableOptimisticUpdates && projectKey && startDate && endDate) {
        const queryKey = epicQueryKeys.list({ projectKey, startDate, endDate });
        const previousData =
          queryClient.getQueryData<AvailableEpicsResponse>(queryKey);

        if (previousData?.success && previousData.data?.epics) {
          // Apply optimistic update
          queryClient.setQueryData(
            queryKey,
            (oldData: AvailableEpicsResponse | undefined) => {
              if (!oldData?.success || !oldData.data?.epics) {
                return oldData;
              }

              const epicExists = oldData.data.epics.some(
                (epic) => epic.key === epicKey
              );
              if (!epicExists) {
                return oldData;
              }

              const updatedEpics = oldData.data.epics.map((epic) => {
                if (epic.key === epicKey) {
                  return {
                    ...epic,
                    analysisStatus: {
                      isAnalyzed: true,
                      analysisDate: new Date().toISOString(),
                      analysisId: null, // Will be updated with real ID on success
                    },
                  };
                }
                return epic;
              });

              return {
                ...oldData,
                data: {
                  ...oldData.data,
                  epics: updatedEpics,
                  summary: {
                    ...oldData.data.summary,
                    analyzed: oldData.data.summary.analyzed + 1,
                    notAnalyzed: Math.max(
                      0,
                      oldData.data.summary.notAnalyzed - 1
                    ),
                  },
                },
              };
            }
          );

          // Create rollback function
          rollback = () => {
            queryClient.setQueryData(queryKey, previousData);
          };
        }
      }

      try {
        const result = await apiFetch<EpicAnalysisResponse>(
          apiUrl,
          `/epics/${epicKey}/analyze`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        // Update with real analysis ID if optimistic update was applied
        if (
          enableOptimisticUpdates &&
          projectKey &&
          startDate &&
          endDate &&
          result.success &&
          result.analysisId
        ) {
          const queryKey = epicQueryKeys.list({
            projectKey,
            startDate,
            endDate,
          });
          queryClient.setQueryData(
            queryKey,
            (oldData: AvailableEpicsResponse | undefined) => {
              if (!oldData?.success || !oldData.data?.epics) {
                return oldData;
              }

              const updatedEpics = oldData.data.epics.map((epic) => {
                if (epic.key === epicKey) {
                  return {
                    ...epic,
                    analysisStatus: {
                      ...epic.analysisStatus,
                      analysisId: result.analysisId,
                    },
                  };
                }
                return epic;
              });

              return {
                ...oldData,
                data: {
                  ...oldData.data,
                  epics: updatedEpics,
                },
              };
            }
          );
        }

        return result;
      } catch (error) {
        // Rollback optimistic update on error
        if (rollback) {
          rollback();
        }
        throw error;
      }
    },
    onSuccess: (data, epicKey) => {
      // Only invalidate if optimistic updates are disabled
      if (!enableOptimisticUpdates) {
        queryClient.invalidateQueries({
          queryKey: epicQueryKeys.lists(),
        });
      }

      // Always invalidate specific epic details
      queryClient.invalidateQueries({
        queryKey: epicQueryKeys.detail(epicKey),
      });
    },
    onError: (error, epicKey) => {
      console.error(`Failed to analyze epic ${epicKey}:`, error);

      // Invalidate queries on error to ensure consistency
      if (projectKey && startDate && endDate) {
        queryClient.invalidateQueries({
          queryKey: epicQueryKeys.list({ projectKey, startDate, endDate }),
        });
      }
    },
  });
}

// ===== Epic Analytics Hooks =====

/**
 * Hook to fetch epic analytics data for visualization
 */
export function useEpicAnalytics(
  options: EpicAnalyticsQueryOptions
): UseQueryResult<EpicAnalyticsData, EpicApiError> {
  const apiUrl = useApi();
  const { projectKey, year, quarter = "all", enabled = true } = options;

  return useQuery({
    queryKey: epicQueryKeys.analyticsData({ projectKey, year, quarter }),
    queryFn: async () => {
      if (!projectKey || !year) {
        throw new Error("Project key and year are required for analytics");
      }

      const params = new URLSearchParams({
        projectKey,
        year: year.toString(),
      });

      // Only add quarter if it's not "all"
      if (quarter !== "all") {
        params.append("quarter", quarter.toString());
      }

      const response = await apiFetch<EpicAnalyticsResponse>(
        apiUrl,
        `/epics/analytics?${params.toString()}`
      );

      if (!response.success || !response.data) {
        throw new Error(
          response.message || "Failed to fetch epic analytics data"
        );
      }

      return response.data;
    },
    enabled: enabled && !!projectKey && !!year,
    staleTime: 20 * 60 * 1000, // 20 minutes - analytics data doesn't change frequently
    cacheTime: 60 * 60 * 1000, // 60 minutes - keep in cache longer for navigation
    refetchOnWindowFocus: false, // Don't refetch on window focus for analytics
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    // Performance optimizations
    keepPreviousData: true, // Keep previous data while fetching new data
    notifyOnChangeProps: ["data", "error", "isLoading"], // Only re-render on specific prop changes
    retry: (failureCount, error) => {
      // Don't retry on validation errors (4xx)
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
}

// ===== Utility Functions =====

/**
 * Transforms AvailableEpicsResponse to EpicListItem array for grid display
 */
export function transformToEpicListItems(response: AvailableEpicsResponse) {
  if (!response.success || !response.data) {
    return [];
  }

  return response.data.epics.map((epic) => ({
    key: epic.key,
    title: epic.title,
    projectKey: epic.project.key,
    completionDate: epic.completionDate,
    analysisStatus: {
      isAnalyzed: epic.analysisStatus.isAnalyzed,
      analysisDate: epic.analysisStatus.analysisDate,
    },
    // Metrics will be populated from analysis data if available
    metrics: undefined,
  }));
}

/**
 * Calculates epic statistics from available epics response
 */
export function calculateEpicStats(response: AvailableEpicsResponse) {
  if (!response.success || !response.data) {
    return {
      totalEpics: 0,
      analyzed: 0,
      notAnalyzed: 0,
      analysisPercentage: 0,
    };
  }

  const { summary } = response.data;
  const analysisPercentage =
    summary.totalFound > 0
      ? Math.round((summary.analyzed / summary.totalFound) * 100)
      : 0;

  return {
    totalEpics: summary.totalFound,
    analyzed: summary.analyzed,
    notAnalyzed: summary.notAnalyzed,
    analysisPercentage,
  };
}

/**
 * Transforms batch status response to simplified BatchStatus for UI
 */
export function transformBatchStatus(response: EnhancedBatchStatusResponse) {
  if (!response.success) {
    return null;
  }

  const { data } = response;
  return {
    batchId: response.batchId,
    status: data.status,
    progress: {
      completed: data.summary.completed + data.summary.failed,
      total: data.summary.totalEpics,
      percentage: data.metrics.progressPercentage,
    },
    estimatedTimeRemaining: data.metrics.estimatedTimeRemaining,
    metrics: {
      processingRate: data.metrics.processingRate,
      averageProcessingTime: data.metrics.averageProcessingTime,
      successRate: data.metrics.successRate,
      elapsedTime: data.metrics.elapsedTime,
    },
  };
}

/**
 * Checks if a batch is actively processing
 */
export function isBatchActive(status: string): boolean {
  return status === "queued" || status === "processing";
}

/**
 * Gets appropriate refetch interval based on batch status
 */
export function getBatchRefetchInterval(status: string): number | false {
  if (isBatchActive(status)) {
    return 5000; // Poll every 5 seconds for active batches
  }
  return false; // Don't poll for completed/failed batches
}

/**
 * Prefetch epic details for better performance
 */
export function usePrefetchEpicDetails() {
  const apiUrl = useApi();
  const queryClient = useQueryClient();

  return useCallback(
    (epicKey: string) => {
      queryClient.prefetchQuery({
        queryKey: epicQueryKeys.detail(epicKey),
        queryFn: async () => {
          return apiFetch<CosmosEpicAnalysisDocument>(
            apiUrl,
            `/epics/${epicKey}`
          );
        },
        staleTime: 15 * 60 * 1000, // 15 minutes
      });
    },
    [apiUrl, queryClient]
  );
}

/**
 * Invalidate epic-related queries for cache management
 */
export function useInvalidateEpicQueries() {
  const queryClient = useQueryClient();

  return {
    invalidateEpicLists: useCallback(() => {
      queryClient.invalidateQueries({
        queryKey: epicQueryKeys.lists(),
      });
    }, [queryClient]),

    invalidateEpicDetails: useCallback(
      (epicKey?: string) => {
        if (epicKey) {
          queryClient.invalidateQueries({
            queryKey: epicQueryKeys.detail(epicKey),
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: epicQueryKeys.details(),
          });
        }
      },
      [queryClient]
    ),

    invalidateEpicAnalytics: useCallback(
      (filters?: {
        projectKey?: string;
        year?: number;
        quarter?: "all" | 1 | 2 | 3 | 4;
      }) => {
        if (filters) {
          queryClient.invalidateQueries({
            queryKey: epicQueryKeys.analyticsData(filters),
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: epicQueryKeys.analytics(),
          });
        }
      },
      [queryClient]
    ),

    invalidateAllEpicQueries: useCallback(() => {
      queryClient.invalidateQueries({
        queryKey: epicQueryKeys.all,
      });
    }, [queryClient]),
  };
}
/**
 * Prefetch epic analytics data for better performance
 */
export function usePrefetchEpicAnalytics() {
  const apiUrl = useApi();
  const queryClient = useQueryClient();

  return useCallback(
    (
      projectKey: string,
      year: number,
      quarter: "all" | 1 | 2 | 3 | 4 = "all"
    ) => {
      queryClient.prefetchQuery({
        queryKey: epicQueryKeys.analyticsData({ projectKey, year, quarter }),
        queryFn: async () => {
          const params = new URLSearchParams({
            projectKey,
            year: year.toString(),
          });

          if (quarter !== "all") {
            params.append("quarter", quarter.toString());
          }

          const response = await apiFetch<EpicAnalyticsResponse>(
            apiUrl,
            `/epics/analytics?${params.toString()}`
          );

          if (!response.success || !response.data) {
            throw new Error(
              response.message || "Failed to fetch epic analytics data"
            );
          }

          return response.data;
        },
        staleTime: 15 * 60 * 1000, // 15 minutes
      });
    },
    [apiUrl, queryClient]
  );
}
